import api from "../services/authService";
import { toApiAbsoluteUrl } from "../components/Document/DocumentUtils";

/**
 * Board appeal review — the Court of Appeals sign-up / turn-down pile.
 *
 * Same contract as the intake review queue: refusals come back as
 * `{ error: "..." }`, and a turn-down without a reason is one of them.
 */

const raise = (error, fallback) => {
  const message =
    error.response?.data?.error || error.response?.data?.message || fallback;
  throw { message, status: error.response?.status || 500 };
};

export const fetchBoardAppealListAPI = async (scope = "pending") => {
  try {
    return await api.get(
      `/board-appeal-reviews/?scope=${encodeURIComponent(scope)}`
    );
  } catch (error) {
    return raise(error, "Failed to load the Board appeal pile");
  }
};

export const fetchBoardAppealCardAPI = async (id) => {
  try {
    return await api.get(`/board-appeal-reviews/${id}/`);
  } catch (error) {
    return raise(error, "Failed to open that Board decision");
  }
};

// The emails about this referral that were filed from Matthew's mailbox.
export const fetchBoardAppealCorrespondenceAPI = async (id) => {
  try {
    return await api.get(`/board-appeal-reviews/${id}/correspondence/`);
  } catch (error) {
    return raise(error, "Failed to load the emails for this client");
  }
};

// One of those emails in full, read from the copy filed in WHG Docs: what
// it says, the quoted thread under it, and its attachments.
export const fetchBoardAppealEmailAPI = async (id, emailId) => {
  try {
    return await api.get(`/board-appeal-reviews/${id}/correspondence/${emailId}/`);
  } catch (error) {
    return raise(error, "Could not open that email");
  }
};

// Every attachment on the card's emails (each once), and the client's other
// copies of the Board decision that could be used instead.
export const fetchBoardAppealFilesAPI = async (id) => {
  try {
    return await api.get(`/board-appeal-reviews/${id}/files/`);
  } catch (error) {
    return raise(error, "Could not list this client's files");
  }
};

// Makes another copy the card's one Board decision: { documentId } for a
// copy in the client's file, { emailId, index } for an email attachment.
export const pickBoardAppealDecisionAPI = async (id, choice) => {
  try {
    return await api.post(`/board-appeal-reviews/${id}/decision/`, choice);
  } catch (error) {
    return raise(error, "Could not switch the Board decision");
  }
};

// The middle viewer fetches this itself, with the sign-in cookie.
export const boardAppealAttachmentUrl = (id, emailId, index) =>
  toApiAbsoluteUrl(
    `/api/board-appeal-reviews/${id}/correspondence/${emailId}/attachments/${index}/`
  );

// Firm people whose name or wernerhoffman.com address starts with what was typed.
export const searchFirmPeopleAPI = async (query) => {
  try {
    return await api.get(
      `/board-appeal-reviews/staff-directory/?q=${encodeURIComponent(query)}`
    );
  } catch (error) {
    return raise(error, "Could not look up firm addresses");
  }
};

// Answers one of those emails from Matthew's Outlook, in its own thread.
export const replyBoardAppealCorrespondenceAPI = async (id, emailId, { body, to, cc }) => {
  try {
    return await api.post(
      `/board-appeal-reviews/${id}/correspondence/${emailId}/reply/`,
      { body, to, cc }
    );
  } catch (error) {
    return raise(error, "The reply was not sent");
  }
};

export const decideBoardAppealAPI = async (id, { action, note, boardDate }) => {
  try {
    return await api.post(`/board-appeal-reviews/${id}/decide/`, {
      action,
      note,
      ...(boardDate ? { board_date: boardDate } : {}),
    });
  } catch (error) {
    return raise(error, "Failed to record that decision");
  }
};

// The scheduled pull runs every five minutes; this is for someone looking
// at the pile right now. It returns the pull's own outcome so a Litify
// failure shows on screen instead of as an empty list.
export const refreshBoardAppealsAPI = async () => {
  try {
    return await api.post(`/board-appeal-reviews/refresh/`, {});
  } catch (error) {
    return raise(error, "Could not check Litify");
  }
};
